/**
 * FDV — cerebro de ofertas (`fdv-offer-state`). Doc 16 §3 / §10.6 paso 1.
 *
 * Headless. Una sola instancia global (se carga desde theme.liquid vía
 * snippets/fdv-gift-engine.liquid, junto al motor de regalos — patch #24).
 *
 * Qué hace:
 *   1. Lee la config de ofertas ya parseada por el motor de regalos
 *      (`window.FDV.gifts.offers`, del metaobjeto `fdv_gift_offer`). Fallback:
 *      parsea el <script id="fdv-gift-offers"> directo.
 *   2. Se suscribe a `fdv:gifts:state` (contrato B del motor C1) para las
 *      ofertas de umbral: progreso, distancia al umbral, estado.
 *   3. Detecta qué **descuentos automáticos** están aplicados AHORA leyendo el
 *      carrito que ya trajo el motor (`window.FDV.gifts.cart`) — sin fetch propio.
 *   4. Publica una vista unificada por oferta con superficie promocional en
 *      `window.FDV.offers` (+ `getOffer`, `bestOffer`) y evento `fdv:offer:state`.
 *
 * Lo consumen las superficies PASIVAS (doc 16 §3): barra de anuncio, overlay del
 * hero, barra del carrito, modal de "activar promo". NO muta el carrito, NO toca
 * DOM de UI. Si truena, el motor de regalos y el carrito siguen intactos.
 */

const EVENT = 'fdv:offer:state';

const log = (...a) => {
  if (window.FDV?.gifts?.debug) console.log('[fdv-offers]', ...a);
};

/* --------------------------------------------------------------- helpers -- */

function money(cents, currency) {
  const v = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency || window.FDV?.gifts?.currency || 'MXN',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    }).format(v);
  } catch {
    return `$${Math.round(v)}`;
  }
}

/** Normaliza títulos de descuento: sin acentos, espacios colapsados, mayúsculas.
 *  Los títulos de descuento son texto libre → tolerar acentos/espacios de más
 *  entre lo que escribe comercial (`auto_discount_title`) y el título real. */
function norm(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/** Sustituye los tokens de progreso ({{restante}}, {{restante_cantidad}}, {{meta}}). */
function renderTokens(tpl, { remainingAmount, remainingQty, threshold }, currency) {
  if (!tpl || tpl.indexOf('{{') === -1) return tpl || '';
  return tpl
    .replace(/\{\{\s*restante\s*\}\}/g, money(remainingAmount, currency))
    .replace(/\{\{\s*restante_cantidad\s*\}\}/g, String(remainingQty ?? 0))
    .replace(/\{\{\s*meta\s*\}\}/g, threshold != null ? money(threshold, currency) : '');
}

/** Parseo de respaldo si el motor de regalos no cargó / erró. */
function parseRaw() {
  const el = document.getElementById('fdv-gift-offers');
  if (!el) return [];
  try {
    const raw = JSON.parse(el.textContent || '{}');
    const list = Array.isArray(raw) ? raw : raw.offers || [];
    return list.map((o) => ({
      id: String(o.id ?? o.handle ?? ''),
      internalName: o.internal_name || '',
      priority: o.priority == null ? 100 : Number(o.priority),
      triggerType: o.trigger_type || 'cart_subtotal',
      endAt: o.end_at ? Number(o.end_at) : null,
      autoDiscountTitle: (o.auto_discount_title || '').trim(),
      unlockedHeadline: o.unlocked_headline || '',
      progressText: o.progress_text || '',
      promoSurface: o.promo_surface === true,
      promoHeadline: o.promo_headline || '',
      promoBody: o.promo_body || '',
      promoCtaLabel: o.promo_cta_label || '',
      promoCtaUrl: o.promo_cta_url || '',
      promoImage: o.promo_image || '',
    }));
  } catch {
    return [];
  }
}

/** Títulos de descuentos automáticos (o de script) presentes en el carrito.
 *  `/cart.js` trae `type: ''` (no `'automatic'`) en el `discount_application`
 *  de línea de al menos los descuentos automáticos BXGY (verificado 2026-09-16
 *  con `2nda_50Off`, campaña Festín de Venus) — inconsistencia de la API de
 *  Shopify para ese mecanismo, no del tema. Se acepta vacío también; lo que
 *  SÍ descarta un código tecleado por el cliente es `type: 'discount_code'`. */
function automaticTitles(cart) {
  const titles = new Set();
  if (!cart) return titles;
  const consider = (app) => {
    const da = app?.discount_application || app;
    if (!da) return;
    if (da.type !== 'discount_code') titles.add(norm(da.title));
  };
  (cart.cart_level_discount_applications || []).forEach(consider);
  (cart.discount_applications || []).forEach(consider);
  for (const it of cart.items || []) {
    for (const alloc of it.line_level_discount_allocations || []) consider(alloc);
  }
  return titles;
}

/** Quita tokens sin sustituir para que nunca lleguen crudos a una superficie.
 *  Excepción: {{cuenta_regresiva}} / {{termina}} — los resuelve la superficie
 *  pasiva (fdv-promo-surfaces.js) con la cuenta regresiva viva de la oferta. */
const stripTokens = (t) =>
  String(t || '')
    .replace(/\{\{(?!\s*(?:cuenta_regresiva|termina)\s*\}\})[^}]*\}\}/g, '')
    .replace(/\s+/g, ' ')
    .trim();

function messageFor(view, currency) {
  const s = view.surface;
  let msg;
  if (view.qualifies) {
    // Desbloqueado: gana el `unlocked_headline` (celebración) sobre el `promo_headline`.
    msg = view.unlockedHeadline || s.headline || '¡Promoción activa!';
  } else if (view.kind === 'threshold' && view.remainingAmount > 0) {
    if (view.threshold != null && view.remainingAmount >= view.threshold && s.headline) {
      // Nada agregado aún: "Agrega $699…" es presuntuoso → headline promocional.
      // En cuanto hay algo en el carrito pasa al progreso concreto.
      msg = s.headline;
    } else {
      const tokens = { remainingAmount: view.remainingAmount, remainingQty: view.remainingQty, threshold: view.threshold };
      msg =
        view.progressText ||
        renderTokens(s.body, tokens, currency) ||
        `Te faltan ${money(view.remainingAmount, currency)} para tu regalo`;
    }
  } else {
    // Aún sin datos de carrito: no arriesgar un texto de progreso a medias.
    // `s.headline` primero (no `s.body`): este `.message` lo comparten TODAS las
    // superficies (barra, banner del carrito) — solo <fdv-promo-pdp> con
    // `data-prefer-body` prefiere el `promo_body` largo, leyéndolo aparte
    // (`offer.surface.body` en fdv-promo-surfaces.js), no de aquí.
    msg = view.kind === 'threshold' && !view.cartKnown ? s.headline || '' : s.headline || s.body || view.unlockedHeadline || '';
  }
  return stripTokens(msg);
}

/** Rango para elegir qué oferta destacar: "casi" > bloqueada > promo > aplicada. */
const STATUS_RANK = { near: 0, locked: 1, promo: 2, active: 2, unlocked: 3 };

function pickBest(offers, opts = {}) {
  let pool = offers.filter((o) => o.surface.headline || o.surface.body);
  if (opts.excludeApplied) pool = pool.filter((o) => o.discountApplied !== true);
  if (opts.status) pool = pool.filter((o) => o.status === opts.status);
  return (
    pool
      .slice()
      .sort((a, b) => (STATUS_RANK[a.status] ?? 5) - (STATUS_RANK[b.status] ?? 5) || a.priority - b.priority)[0] || null
  );
}

/* --------------------------------------------------------------- cerebro -- */

class OfferState {
  #offers = [];
  #giftState = [];
  #cart = null;

  init() {
    window.FDV = window.FDV || {};

    document.addEventListener('fdv:gifts:state', (e) => {
      this.#giftState = Array.isArray(e.detail?.state) ? e.detail.state : [];
      if (e.detail?.cart) this.#cart = e.detail.cart;
      this.#publish();
    });

    // El motor de regalos pudo haber publicado antes de que cargáramos.
    const g = window.FDV.gifts;
    if (g) {
      if (Array.isArray(g.state)) this.#giftState = g.state;
      if (g.cart) this.#cart = g.cart;
    }

    this.#publish();
  }

  /** Prefiere la config normalizada del motor; si no trae los campos promo, reparsea. */
  #pull() {
    const eng = window.FDV?.gifts?.offers;
    this.#offers = Array.isArray(eng) && eng.length && eng[0] && 'promoSurface' in eng[0] ? eng : parseRaw();
  }

  #view() {
    const currency = window.FDV?.gifts?.currency || this.#cart?.currency || 'MXN';
    const gsById = new Map(this.#giftState.map((s) => [s.id, s]));
    const autoTitles = automaticTitles(this.#cart);
    const cartKnown = !!this.#cart;

    return this.#offers
      .filter((o) => o.promoSurface || o.promoHeadline)
      .map((o) => {
        const gs = gsById.get(o.id);
        const wantTitle = norm(o.autoDiscountTitle);

        let discountApplied = null; // null = desconocido (sin carrito aún)
        if (wantTitle && cartKnown) discountApplied = autoTitles.has(wantTitle);
        else if (gs) discountApplied = gs.status === 'unlocked';

        const cfgKind =
          o.triggerType === 'cart_subtotal' || o.triggerType === 'collection_quantity'
            ? 'threshold'
            : o.triggerType === 'promo_only'
              ? 'promo'
              : 'binary';

        let status = gs ? gs.status : 'promo';
        if (!gs && discountApplied === true) status = 'unlocked';

        const view = {
          id: o.id,
          internalName: o.internalName || '',
          kind: gs ? gs.kind : cfgKind,
          status, // locked | near | unlocked | promo
          qualifies: gs ? gs.status === 'unlocked' : discountApplied === true,
          discountApplied,
          progress: gs ? gs.progress : discountApplied ? 1 : 0,
          remainingAmount: gs ? gs.remainingAmount : 0,
          remainingQty: gs ? gs.remainingQty : 0,
          threshold: gs ? gs.threshold : null,
          endsAt: o.endAt || null,
          priority: o.priority == null ? 100 : Number(o.priority),
          unlockedHeadline: o.unlockedHeadline || '',
          progressText: gs ? gs.progressText : o.progressText || '',
          surface: {
            headline: o.promoHeadline || o.unlockedHeadline || '',
            body: o.promoBody || '',
            ctaLabel: o.promoCtaLabel || '',
            ctaUrl: o.promoCtaUrl || '',
            image: o.promoImage || '',
          },
          cartKnown,
        };
        view.message = messageFor(view, currency);
        return view;
      })
      .sort((a, b) => a.priority - b.priority);
  }

  #publish() {
    this.#pull();
    const offers = this.#view();

    window.FDV.offers = offers;
    window.FDV.getOffer = (id) => offers.find((o) => o.id === id) || null;
    window.FDV.bestOffer = (opts) => pickBest(offers, opts);

    log('offers:', offers);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { offers, cart: this.#cart } }));
  }
}

/* ------------------------------------------------------------------ init -- */

const state = new OfferState();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => state.init(), { once: true });
} else {
  state.init();
}
